import type { ButtonProps } from '@mui/material';
import { Button } from '@mui/material';
import { MEASUREMENTS_STATS_MODES, type MeasurementsStats } from '@vy/api-definition';
import { getSelectorAttribute, usagePageSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import { isNotNullOrUndefined, raise, validateNotEmptyArray, validateNotNullOrUndefined } from '@vy/utils';
import { vyDate } from '@vy/utils';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import React from 'react';
import { useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import type { UseMeasurementsStatsInput } from '../../../../hooks/requests/use-measurements-stats';
import { buildGetMeasurementsStatsRequest } from '../../../../hooks/requests/use-measurements-stats';
import { useFromDayParam } from '../../../../hooks/search-params/use-from-day-param';
import { useToDayParam } from '../../../../hooks/search-params/use-to-day-param';
import { getPrivateApi } from '../../../../services/api';
import { useDataMeteringParamsIds } from '../data-metering-hooks/use-data-metering-params-ids';

type ExportToCsvButtonProps = {} & ButtonProps;

export const ExportToCsvButton: React.FC<ExportToCsvButtonProps> = ({ ...buttonProps }) => {
  const [searchParams] = useSearchParams();

  const [paramsDayFrom] = useFromDayParam();
  const [paramsDayTo] = useToDayParam();

  const startTime = paramsDayFrom?.toDate();
  const endTime = paramsDayTo?.toDate();
  const { customersIds, metersIds, eventNames, page, pageSize } = useDataMeteringParamsIds(searchParams);

  const getMeasurementsStatsExportMutation = useMutation({
    mutationFn: async () => {
      const api = getPrivateApi();

      const request = buildExportRequest({
        startTime,
        endTime,
        customersIds,
        eventNames,
        metersIds,
        mode: MEASUREMENTS_STATS_MODES.EXPORT,
        page,
        pageSize,
      });
      const getMeasurementsStatsResponse = await api.getMeasurementsStats(request);

      return getMeasurementsStatsResponse.items;
    },
  });


  const onExportClick = async () => {
    const measurementsStats = await getMeasurementsStatsExportMutation.mutateAsync();

    validateNotNullOrUndefined(startTime);
    validateNotNullOrUndefined(endTime);

    handleExportCsvClick(measurementsStats, startTime, endTime);
  };

  return (
    <Button
      {...getSelectorAttribute(usagePageSelectors.header.downloadCsvButton)}
      variant="contained"
      size="small"
      onClick={onExportClick}
      startIcon={<VyIcon icon="Download" style={{ transform: 'scale(0.8)' }} />}
      {...buttonProps}
      disabled={getMeasurementsStatsExportMutation.isLoading}
    >
      Download
    </Button>
  );
};

function handleExportCsvClick(
  measurementsStats: MeasurementsStats[] | undefined,
  startDay: Date,
  endDay: Date,
) {
  validateNotEmptyArray(measurementsStats, 'measurementsStats');
  const measurementsToExport = measurementsStats.map(buildAggregatedMeasurementForCsv);
  const csv = Papa.unparse(measurementsToExport);
  const fileNameDayRange = formatDatesRange(startDay, endDay);

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const fileName = `Usage Report - ${fileNameDayRange}.csv`;

  saveAs(blob, fileName);
}

function formatDatesRange(startDay: Date, endDay: Date) {
  return vyDate(startDay).format('MM/DD/YY') + ' To ' + vyDate(endDay).format('MM/DD/YY');
}

function buildAggregatedMeasurementForCsv(measurementStat: MeasurementsStats) {
  return {
    Day: measurementStat.day,
    Customer: measurementStat.customer,
    Product: measurementStat.product,
    'Sub Product': measurementStat.productVariant,
    QTY: measurementStat.quantity,
  };
}

function buildExportRequest(input: UseMeasurementsStatsInput) {
  const { startTime, endTime, customersIds, eventNames, metersIds, page, pageSize } = input;

  const isParamsValid =
    isNotNullOrUndefined(startTime) &&
    isNotNullOrUndefined(endTime) &&
    (isNotNullOrUndefined(customersIds) || isNotNullOrUndefined(eventNames));

  if (!isParamsValid) {
    raise('Invalid input');
  }

  const getMeasurementsStatsRequest = buildGetMeasurementsStatsRequest(
    startTime,
    endTime,
    customersIds,
    eventNames,
    metersIds,
    MEASUREMENTS_STATS_MODES.EXPORT,
    page,
    pageSize,
  );

  return getMeasurementsStatsRequest;
}
